import { Form } from "@/components/ui/form";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { NewDiff } from "@/features/FormCard/NewDiff";
import { useCurrencyOptions } from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { dynamicNewFormConfigWithoutId } from "./edit-form-conf";
import { fieldTranslations } from "./translations";
import { Clients } from "@/lib/clients";

import { Tables } from "@/gen/tableNames/tables";

export function D1StaticDataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    selectFieldsConfig,
    isLoadingTranslations,
    optionsMap,
    options,
  } = useDynamicForm({
    client: Clients.D1,
    table_name: Tables.D1_STATIC_DATA,
    formConfig: dynamicNewFormConfigWithoutId,
    isNew: true,
  });

  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch("asset_class_2");

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Actif
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t(`${Tables.D1_STATIC_DATA}.sections.general_information`, {
                    ns: "tables",
                    defaultValue: "Informations générales",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <InputField
                      name="name"
                      label={t(`${Tables.D1_STATIC_DATA}.columns.name`, {
                        ns: "tables",
                        defaultValue: "Nom",
                      })}
                      isRequired={true}
                      capitalize={true}
                      control={form.control}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <AutocompleteLabelField
                      name="currency"
                      label={t(`${Tables.D1_STATIC_DATA}.columns.currency`, {
                        ns: "tables",
                        defaultValue: "Devise",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  <InputField
                    name="isin"
                    label={t(`${Tables.D1_STATIC_DATA}.columns.isin`, {
                      ns: "tables",
                      defaultValue: "ISIN",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="code"
                    label={t(`${Tables.D1_STATIC_DATA}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="ticker"
                    label={t(`${Tables.D1_STATIC_DATA}.columns.ticker`, {
                      ns: "tables",
                      defaultValue: "Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name="d1_issuer_name"
                  label={t(`${Tables.D1_STATIC_DATA}.columns.d1_issuer_name`, {
                    ns: "tables",
                    defaultValue: "Emetteur",
                  })}
                  control={form.control}
                  base_url={`${Clients.D1}/${Tables.D1_ISSUERS}`}
                  id_column="issuer_id"
                  column_name="name"
                />
                <SelectFieldWithSection
                  listboxWrapperClassNames="!h-[450px] !max-h-none"
                  primary_key="asset_class_1"
                  name="asset_class_2"
                  label={t(`${Tables.D1_STATIC_DATA}.columns.name`, {
                    ns: "tables",
                    defaultValue: "Classe d'actifs",
                  })}
                  control={form.control}
                  sections={optionsMap.asset_class_2}
                  placeholder={t("form.placeholders.select")}
                  isRequired
                />
                <Divider />
                <Label>
                  {t("sections.technical_info", {
                    defaultValue: "Informations techniques",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="strategy_id"
                    label={t(`${Tables.D1_STATIC_DATA}.columns.strategy_id`, {
                      ns: "tables",
                      defaultValue: "ID de la stratégie",
                    })}
                    control={form.control}
                    type="number"
                  />
                </div>
                <DynamicFields
                  primaryValue={assetClass1}
                  fieldValue={assetClass2}
                  control={form.control}
                  config={selectFieldsConfig?.asset_class_2}
                  isLoadingTranslations={isLoadingTranslations}
                  options={options}
                />
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["issuer_id"],
                  fieldTranslations,
                  Tables.D1_STATIC_DATA,
                )
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
